import clsx from "clsx";
import type { PageId } from "../components/Sidebar";
import { useTimezone } from "../lib/datetime";

interface MobileSidebarProps {
  page: PageId;
  onChange: (p: PageId) => void;
  onOpenSettings: () => void;
  onOpenDownload: () => void;
  onOpenResync: () => void;
  onOpenDiary: () => void;
  onOpenAlerts?: () => void;
  onOpenPipeSchedule?: () => void;
  open: boolean;
  onClose: () => void;
}

interface NavEntry {
  id: PageId;
  label: string;
  hint: string;
}

const PAGES: NavEntry[] = [
  { id: "dashboard",    label: "Dashboard",        hint: "Live TBM controls" },
  { id: "drive",        label: "Drive parameters", hint: "Plot multiple parameters" },
  { id: "pumps",        label: "Pumps",            hint: "Feed and slurry pumps" },
  { id: "lubrication",  label: "Lubrication",      hint: "Bentonite delivery" },
  { id: "jackingForce", label: "Jacking Force",    hint: "Live parameter prediction" },
];

export function MobileSidebar({
  page,
  onChange,
  onOpenSettings,
  onOpenDownload,
  onOpenResync,
  onOpenDiary,
  onOpenAlerts,
  onOpenPipeSchedule,
  open,
  onClose,
}: MobileSidebarProps) {
  const tz = useTimezone();
  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 z-40 bg-black/40 transition-opacity",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-[280px] max-w-[88vw] bg-surface1 border-r border-border1 shadow-xl flex flex-col",
          "transition-transform duration-200 will-change-transform",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <nav className="flex-1 py-4 overflow-y-auto">
          <NavSection title="Overview">
            {PAGES.map((p) => (
              <NavItem
                key={p.id}
                active={page === p.id}
                onClick={() => onChange(p.id)}
                label={p.label}
                hint={p.hint}
              />
            ))}
          </NavSection>

          <NavSection title="Tools">
            {onOpenAlerts && (
              <NavItem onClick={onOpenAlerts} label="Warnings" hint="Threshold alerts & email" />
            )}
            <NavItem onClick={onOpenDiary}    label="Diary"        hint="Operator log entries" />
            {onOpenPipeSchedule && (
              <NavItem onClick={onOpenPipeSchedule} label="Pipe schedule" hint="Detect & pin pipe changes" />
            )}
            <NavItem onClick={onOpenSettings} label="Preprocessing" hint="Tune filters & aggregation" />
            <NavItem onClick={onOpenDownload} label="Download"     hint="Export CSV" />
            <NavItem onClick={onOpenResync}   label="Drive setup"  hint="Resync & set drive parameters" />
          </NavSection>
        </nav>

        <div className="px-5 py-3 border-t border-border1 text-[10px] text-dim tracking-widest uppercase">
          Ward &amp; Burke
          <br />
          <span className="text-muted">Tunnel Engineering</span>
          <br />
          <span
            className="block mt-1 normal-case tracking-normal text-[10.5px] text-muted"
            title="Configured display timezone"
          >
            tz: <span className="mono-nums text-text">{tz}</span>
          </span>
        </div>
      </aside>
    </>
  );
}

function NavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="px-5 mb-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-dim">
        {title}
      </div>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

function NavItem({
  active = false,
  onClick,
  label,
  hint,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className={clsx(
          "group relative w-full text-left pl-5 pr-3 py-3 flex flex-col",
          "transition-colors",
          active ? "bg-surface2" : "hover:bg-surface2/60 active:bg-surface2/60",
        )}
      >
        <span
          className={clsx(
            "absolute left-0 top-0 bottom-0 w-[3px]",
            active ? "bg-wb" : "bg-transparent",
          )}
        />
        <span
          className={clsx(
            "text-[14px] font-medium",
            active ? "text-text" : "text-muted",
          )}
        >
          {label}
        </span>
        <span className="text-[11px] text-dim">{hint}</span>
      </button>
    </li>
  );
}
